import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Search, Filter, X, ChevronDown, Mail, ListTodo, ArrowRight, Calendar, User, ExternalLink, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { LeadDetailPanel } from './leads/LeadDetailPanel';
import { RfpTrackerTab } from './leads/RfpTrackerTab';
import PipelineSummaryTab from './leads/PipelineSummaryTab';

const STAGE_COLORS = {
  'New Lead': { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/25', dot: 'bg-blue-400' },
  'Researching': { bg: 'bg-cyan-500/15', text: 'text-cyan-400', border: 'border-cyan-500/25', dot: 'bg-cyan-400' },
  'Outreach': { bg: 'bg-violet-500/15', text: 'text-violet-400', border: 'border-violet-500/25', dot: 'bg-violet-400' },
  'Under Review': { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/25', dot: 'bg-amber-400' },
  'Won': { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/25', dot: 'bg-emerald-400' },
  'Lost': { bg: 'bg-zinc-500/15', text: 'text-zinc-400', border: 'border-zinc-500/25', dot: 'bg-zinc-400' },
};

const SOURCE_OPTIONS = ['Google', 'Word of Mouth', 'Social Media', 'Email Blast', 'Conference', 'LinkedIn', 'Referral', 'Website'];

export default function LeadsPage() {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [sourceOpts, setSourceOpts] = useState(SOURCE_OPTIONS);
  const [search, setSearch] = useState('');
  const [filterOwner, setFilterOwner] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [collapsedStages, setCollapsedStages] = useState({});
  const [stageDescriptions, setStageDescriptions] = useState({});
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchLeads = useCallback(() => {
    api.get('/leads').then(r => setLeads(r.data || [])).catch(() => {});
  }, []);

  const fetchMeta = useCallback(() => {
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => {});
    api.get('/leads/pipeline-stages').then(r => {
      const all = [...(r.data.default || []), ...(r.data.custom || [])];
      setStages(all);
      setStageDescriptions(r.data.descriptions || {});
    }).catch(() => setStages(['New Lead', 'Researching', 'Outreach', 'Under Review', 'Won', 'Lost']));
    api.get('/leads/tags').then(r => setAllTags(r.data || [])).catch(() => {});
    api.get('/leads/source-options').then(r => {
      setSourceOpts([...(r.data.default || SOURCE_OPTIONS), ...(r.data.custom || [])]);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchLeads(); fetchMeta(); }, [fetchLeads, fetchMeta]);

  // Deep-link: /leads?lead=<id> auto-opens that lead's detail panel.
  // Used by the proposal "From Lead" link, the global Cmd+K search,
  // and anywhere else we want to point users at a specific lead record.
  const leadIdParam = searchParams.get('lead');
  useEffect(() => {
    if (!leadIdParam) return;
    api.get(`/leads/${leadIdParam}`)
      .then(r => { setSelectedLead(r.data); setDetailOpen(true); setCreateOpen(false); })
      .catch(() => toast.error('Lead not found'));
    // Strip the param so a refresh doesn't reopen.
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('lead');
      return next;
    }, { replace: true });
  }, [leadIdParam]);

  const openLead = (lead) => {
    // Open the detail panel for EVERY lead, including Won ones. The panel
    // shows the original lead data + a button to jump to the linked proposal.
    setSelectedLead(lead);
    setDetailOpen(true);
    setCreateOpen(false);
  };

  const openCreate = () => {
    setSelectedLead(null);
    setDetailOpen(true);
    setCreateOpen(true);
  };

  const handleDetailClose = (open) => {
    if (!open) {
      setDetailOpen(false);
      setSelectedLead(null);
      setCreateOpen(false);
      fetchLeads();
    }
  };

  const handleLeadRefresh = (updatedLead) => {
    setSelectedLead(updatedLead);
    fetchLeads();
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await api.put(`/leads/${leadId}`, { pipeline_stage: newStage });
      fetchLeads();
    } catch (e) { toast.error('Error moving lead'); }
  };

  const toggleCollapse = (stage) => {
    setCollapsedStages(prev => ({ ...prev, [stage]: !prev[stage] }));
  };

  const hasActiveFilters = filterOwner !== 'all' || filterTag !== 'all' || filterSource !== 'all' || search.trim() !== '';
  const clearFilters = () => { setFilterOwner('all'); setFilterTag('all'); setFilterSource('all'); setSearch(''); };

  const filteredLeads = useMemo(() => {
    return leads.filter(l => {
      if (search.trim()) {
        const s = search.toLowerCase();
        if (!(l.contact_name || '').toLowerCase().includes(s) &&
            !(l.company || '').toLowerCase().includes(s) &&
            !(l.email || '').toLowerCase().includes(s)) return false;
      }
      if (filterOwner !== 'all' && l.owner_id !== filterOwner) return false;
      if (filterTag !== 'all' && !(l.tags || []).includes(filterTag)) return false;
      if (filterSource !== 'all' && l.source !== filterSource) return false;
      return true;
    });
  }, [leads, search, filterOwner, filterTag, filterSource]);

  const groupedByStage = useMemo(() => {
    const map = {};
    for (const stage of stages) map[stage] = [];
    for (const l of filteredLeads) {
      const stage = l.pipeline_stage || 'New Lead';
      if (!map[stage]) map[stage] = [];
      map[stage].push(l);
    }
    return map;
  }, [filteredLeads, stages]);

  const activeOwners = useMemo(() => {
    const ownerIds = new Set(leads.map(l => l.owner_id).filter(Boolean));
    return users.filter(u => ownerIds.has(u.id));
  }, [leads, users]);

  const activeTags = useMemo(() => {
    const tags = new Set();
    leads.forEach(l => (l.tags || []).forEach(t => tags.add(t)));
    return [...tags].sort();
  }, [leads]);

  const isDueSoon = (dueDate) => {
    if (!dueDate) return false;
    const days = Math.ceil((new Date(dueDate) - new Date()) / 86400000);
    return days <= 5 && days >= 0;
  };
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date(new Date().toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="leads-page">
      {/* Page Title & Tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-medium tracking-tight">RFP & Leads Pipeline</h1>
          <div className="flex items-center gap-1 mt-2">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'pipeline' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
              onClick={() => setActiveTab('pipeline')}
              data-testid="tab-pipeline"
            >Leads Pipeline</button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'rfps' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
              onClick={() => setActiveTab('rfps')}
              data-testid="tab-rfps"
            >RFPs & Bid Opportunities</button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'pipeline-summary' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
              onClick={() => setActiveTab('pipeline-summary')}
              data-testid="tab-pipeline-summary"
            >Pipeline Summary</button>
          </div>
        </div>
        {activeTab === 'pipeline' && (
          <Button onClick={openCreate} className="bg-primary text-primary-foreground" data-testid="create-lead-button">
            <Plus className="h-4 w-4 mr-1" /> New Lead
          </Button>
        )}
      </div>

      {activeTab === 'rfps' && <RfpTrackerTab />}
      {activeTab === 'pipeline-summary' && <PipelineSummaryTab users={users} />}

      {activeTab === 'pipeline' && (<>
      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border h-9 text-sm" data-testid="leads-search" />
        </div>
        <Button variant="outline" size="sm" className={`h-9 text-xs border-border ${showFilters ? 'bg-primary/10 text-primary' : ''}`} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
          <Filter className="h-3 w-3 mr-1" /> Filters {hasActiveFilters && <Badge className="ml-1 h-4 w-4 p-0 text-xs bg-primary text-primary-foreground rounded-full flex items-center justify-center">!</Badge>}
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground" onClick={clearFilters} data-testid="clear-filters-btn">
            <X className="h-3 w-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      {showFilters && (
        <div className="flex items-center gap-3 flex-wrap p-3 rounded-md border border-border bg-card/50" data-testid="filter-bar">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Owner</label>
            <Select value={filterOwner} onValueChange={setFilterOwner}>
              <SelectTrigger className="w-40 h-8 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all" className="text-xs">All Owners</SelectItem>
                {activeOwners.map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Tag</label>
            <Select value={filterTag} onValueChange={setFilterTag}>
              <SelectTrigger className="w-40 h-8 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all" className="text-xs">All Tags</SelectItem>
                {activeTags.map(t => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Source</label>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="w-40 h-8 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all" className="text-xs">All Sources</SelectItem>
                {sourceOpts.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <TooltipProvider delayDuration={200}>
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '60vh' }} data-testid="pipeline-kanban">
        {stages.map(stage => {
          const stageLeads = groupedByStage[stage] || [];
          const colors = STAGE_COLORS[stage] || STAGE_COLORS['New Lead'];
          const isCollapsed = collapsedStages[stage];
          const description = stageDescriptions[stage];
          return (
            <div key={stage} className="flex-shrink-0 w-72" data-testid={`stage-column-${stage.replace(/\s+/g, '-').toLowerCase()}`}>
              {/* Column Header */}
              <div
                className={`flex items-center justify-between p-2.5 rounded-t-lg border ${colors.border} ${colors.bg} cursor-pointer select-none`}
                onClick={() => toggleCollapse(stage)}
              >
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${colors.dot}`} />
                  {description ? (
                    <Tooltip>
                      <TooltipTrigger asChild onClick={e => e.stopPropagation()}>
                        <span className={`text-xs font-semibold tracking-tight ${colors.text} cursor-help flex items-center gap-1`}>
                          {stage}
                          <Info className={`h-3 w-3 opacity-50`} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                        <p>{description}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className={`text-xs font-semibold tracking-tight ${colors.text}`}>{stage}</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className={`text-xs h-5 px-1.5 ${colors.text} ${colors.border}`}>{stageLeads.length}</Badge>
                  <ChevronDown className={`h-3 w-3 ${colors.text} transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                </div>
              </div>

              {/* Column Body */}
              {!isCollapsed && (
                <div className="border border-t-0 border-border rounded-b-lg bg-card/30 p-1.5 space-y-1.5 min-h-[100px]">
                  {stageLeads.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-6 opacity-50">No leads</p>
                  ) : (
                    stageLeads.map(lead => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onClick={() => openLead(lead)}
                        isDueSoon={isDueSoon(lead.due_date)}
                        isOverdue={isOverdue(lead.due_date)}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </TooltipProvider>
      </>)}

      <LeadDetailPanel
        open={detailOpen}
        onOpenChange={handleDetailClose}
        lead={selectedLead}
        isCreate={createOpen}
        users={users}
        allTags={allTags}
        stages={stages}
        stageDescriptions={stageDescriptions}
        onRefresh={handleLeadRefresh}
        onLeadsRefresh={fetchLeads}
        onTagsRefresh={() => api.get('/leads/tags').then(r => setAllTags(r.data || [])).catch(() => {})}
      />
    </div>
  );
}


function LeadCard({ lead, onClick, isDueSoon, isOverdue }) {
  return (
    <button
      type="button"
      className="w-full text-left p-2.5 rounded-md border border-border bg-background hover:border-primary/30 transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-background"
      onClick={onClick}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="space-y-1.5">
        <div className="flex items-start justify-between gap-1">
          <div className="min-w-0">
            <p className="text-sm font-medium truncate leading-tight">{lead.company || lead.contact_name}</p>
            {lead.company && <p className="text-xs text-muted-foreground truncate">{lead.contact_name}</p>}
          </div>
          {lead.source && <Badge variant="outline" className="text-xs shrink-0 h-4 px-1">{lead.source}</Badge>}
        </div>

        {/* Tags */}
        {(lead.tags || []).length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {lead.tags.slice(0, 3).map(t => (
              <Badge key={t} className="text-xs h-4 px-1.5 bg-primary/10 text-primary border-primary/20">{t}</Badge>
            ))}
            {lead.tags.length > 3 && <Badge variant="outline" className="text-xs h-4 px-1">+{lead.tags.length - 3}</Badge>}
          </div>
        )}

        {/* Meta row */}
        <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
          {lead.owner_name && (
            <span className="flex items-center gap-0.5"><User className="h-2.5 w-2.5" />{lead.owner_name}</span>
          )}
          {lead.due_date && (
            <span className={`flex items-center gap-0.5 ${isOverdue ? 'text-destructive font-semibold' : isDueSoon ? 'text-amber-400 font-medium' : ''}`}>
              <Calendar className="h-2.5 w-2.5" />{lead.due_date}
            </span>
          )}
        </div>

        {/* Indicators */}
        <div className="flex items-center gap-2 text-xs">
          {lead.proposal_number && (
            <span className="flex items-center gap-0.5 text-emerald-400 font-medium">
              <ExternalLink className="h-2.5 w-2.5" />
              {lead.proposal_number}
            </span>
          )}
          {lead.next_steps_total > 0 && (
            <span className="flex items-center gap-0.5 text-muted-foreground">
              <ArrowRight className="h-2.5 w-2.5" />
              {lead.next_steps_completed}/{lead.next_steps_total}
            </span>
          )}
          {lead.todos_total > 0 && (
            <span className="flex items-center gap-0.5 text-muted-foreground">
              <ListTodo className="h-2.5 w-2.5" />
              {lead.todos_completed}/{lead.todos_total}
            </span>
          )}
          {lead.pending_drafts > 0 && (
            <span className="flex items-center gap-0.5 text-amber-400">
              <Mail className="h-2.5 w-2.5" />
              {lead.pending_drafts} draft{lead.pending_drafts > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
